import React, { FC, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentType,
  ChatSuggestion,
  GetChatSuggestionsResponse,
  UserInputResponse,
} from '@dreamai/types';

import api from '../../../api';
import CreateCampaignModal from './CreateCampaignModal';
import CreateCompanyResearchModal from './CreateCompanyResearchModal';
import ErrorState from '../../common/ErrorState';
import { ReactComponent as ArrowRightIcon } from '../../../icons/arrow-right.svg';
import { ReactComponent as CampaignIcon } from '../../../icons/campaign.svg';
import { ReactComponent as SearchIcon } from '../../../icons/agent-search.svg';
import { ReactComponent as CheckIcon } from '../../../icons/check.svg';
import { ReactComponent as PlusIcon } from '../../../icons/plus.svg';
import { ReactComponent as SpinnerIcon } from '../../../icons/spinner.svg';
import './new-thread-modal.scss';

export type CreationType =
  | 'chat'
  | 'trip'
  | 'project'
  | 'autopilot'
  | 'campaign';

const CREATION_TYPE_LABELS: Record<
  CreationType,
  { heading: string; label: string; placeholder: string }
> = {
  chat: {
    heading: 'Create a new chat',
    label: 'Chat name',
    placeholder: 'E.g. Holiday research',
  },
  trip: {
    heading: 'Create a new trip or event',
    label: 'Trip or Event name',
    placeholder: 'E.g. Summer holiday to Italy',
  },
  project: {
    heading: 'Create a new project',
    label: 'Project name',
    placeholder: 'E.g. House move',
  },
  autopilot: {
    heading: 'Create a new autopilot',
    label: 'Autopilot name',
    placeholder: 'E.g. Weekly report digest',
  },
  campaign: {
    heading: 'Create a new campaign',
    label: 'Campaign name',
    placeholder: 'E.g. Q3 outreach campaign',
  },
};

const getPlaceholderForAgentType = (
  agentType?: string,
  creationType?: CreationType,
): string => {
  if (creationType && creationType !== 'chat') {
    return CREATION_TYPE_LABELS[creationType].placeholder;
  }
  switch (agentType) {
    case 'Sales Assistant':
      return 'E.g. Discuss strategy...';
    case 'Personal Assistant':
      return 'E.g. Holiday research';
    default:
      return 'E.g. New project...';
  }
};

interface NewThreadModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  agentName?: string;
  agentImageUrl?: string;
  agentType?: string;
  creationType?: CreationType;
  onThreadCreated?: () => void;
}

const NewThreadModal: FC<NewThreadModalProps> = ({
  isOpen,
  onClose,
  agentId,
  agentName,
  agentImageUrl,
  agentType,
  creationType = 'chat',
  onThreadCreated,
}) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<ChatSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showCompanyResearchModal, setShowCompanyResearchModal] =
    useState(false);

  // Fetch suggestions when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchSuggestions = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        const response = await api.get<GetChatSuggestionsResponse>(
          '/agents/chat-suggestions',
          {
            params: { agent_id: agentId, creation_type: creationType },
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setSuggestions(response.data.suggestions);
        }
      } catch (err) {
        console.error('Error fetching chat suggestions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
    // Focus input when modal opens
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen, agentId, getAccessTokenSilently]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
      setSuggestions([]);
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (threadName: string) => {
    if (!threadName.trim()) return;

    try {
      setSubmitting(true);
      const tightThreadName = threadName.trim();
      const token = await getAccessTokenSilently();
      const response = await api.post<UserInputResponse>(
        '/agents',
        {
          agent_id: agentId,
          message: `SYSTEM_MESSAGE The user will send you the first message in a minute they have named this thread ${tightThreadName}. Send a short message appropriate to that thread name asking them what they want to discuss.
          `,
          thread_name: tightThreadName,
          creation_type: creationType,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success && response.data.thread_id) {
        onThreadCreated?.();
        setSubmitting(false);
        onClose();
        navigate(
          `/conversations?agent_id=${agentId}&thread_id=${response.data.thread_id}`,
        );
      }
    } catch (err) {
      console.error('Error creating thread:', err);
      setSubmitting(false);
      setError(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(inputValue);
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleSuggestionClick = (suggestion: ChatSuggestion) => {
    if (suggestion.specific_action) {
      // Suggestion with action - submit immediately
      handleSubmit(suggestion.text);
    } else {
      // Just fill the input for editing
      setInputValue(suggestion.text);
      inputRef.current?.focus();
    }
  };

  if (!isOpen && !showCampaignModal && !showCompanyResearchModal) return null;

  return (
    <>
      <CreateCampaignModal
        show={showCampaignModal}
        onClose={() => setShowCampaignModal(false)}
        agentName={agentName}
      />
      <CreateCompanyResearchModal
        show={showCompanyResearchModal}
        onClose={() => setShowCompanyResearchModal(false)}
        agentName={agentName}
        agentId={agentId}
        onOpenCampaignModal={() => setShowCampaignModal(true)}
      />
      {isOpen &&
        createPortal(
          <div className='new-thread-modal-overlay' onClick={onClose}>
            <div
              className={`new-thread-modal ${submitting ? 'loading' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className='new-thread-modal-content'>
                <h1>{CREATION_TYPE_LABELS[creationType].heading}</h1>
                <div className='new-thread-input-wrapper'>
                  <div className='agent-avatar'>
                    {submitting ? (
                      <SpinnerIcon className='avatar-spinner' />
                    ) : (
                      agentImageUrl && (
                        <img src={agentImageUrl} alt={agentName} />
                      )
                    )}
                  </div>
                  <div className='new-thread-form-wrapper'>
                    <label className='new-thread-label'>
                      {CREATION_TYPE_LABELS[creationType].label}
                    </label>
                    <textarea
                      ref={inputRef}
                      className='new-thread-input'
                      placeholder={getPlaceholderForAgentType(
                        agentType,
                        creationType,
                      )}
                      value={inputValue}
                      maxLength={60}
                      onChange={(e) => {
                        setInputValue(e.target.value);
                        // Auto-resize - reset to 0 to get accurate scrollHeight
                        e.target.style.height = '0px';
                        e.target.style.height = `${e.target.scrollHeight}px`;
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={submitting}
                      rows={1}
                    />
                  </div>
                  <button
                    className={`new-thread-submit-btn ${inputValue.trim() ? 'has-text' : ''}`}
                    onClick={() => handleSubmit(inputValue)}
                    disabled={submitting || !inputValue.trim()}
                  >
                    <CheckIcon />
                  </button>
                </div>

                {submitting && <div className='new-thread-loading-spinner' />}

                {error && (
                  <ErrorState
                    title='Something went wrong'
                    subtitle='Please try again'
                  />
                )}

                {!submitting &&
                  !error &&
                  suggestions.filter((s) => s.chatName && !s.specific_action)
                    .length > 0 && (
                    <div className='new-thread-suggestions'>
                      {suggestions
                        .filter((s) => s.chatName && !s.specific_action)
                        .map((suggestion, index) => (
                          <button
                            key={index}
                            className='suggestion-pill'
                            onClick={() =>
                              handleSubmit(suggestion.chatName || '')
                            }
                          >
                            {suggestion.text}
                          </button>
                        ))}
                    </div>
                  )}

                {!submitting &&
                  !error &&
                  suggestions.filter((s) => s.specific_action).length > 0 && (
                    <>
                      <h4 className='new-thread-or'>OR</h4>
                      <div className='new-thread-actions'>
                        {suggestions
                          .filter((s) => s.specific_action)
                          .map((suggestion, index) => (
                            <button
                              key={index}
                              className='action-item'
                              onClick={() => {
                                if (
                                  suggestion.specific_action ===
                                  'create_campaign'
                                ) {
                                  onClose();
                                  setShowCampaignModal(true);
                                } else if (
                                  suggestion.specific_action ===
                                  'research_companies'
                                ) {
                                  onClose();
                                  setShowCompanyResearchModal(true);
                                }
                              }}
                            >
                              <span className='action-icon'>
                                {suggestion.specific_action ===
                                'research_companies' ? (
                                  <SearchIcon />
                                ) : (
                                  <CampaignIcon />
                                )}
                              </span>
                              <span className='action-text'>
                                {suggestion.text}
                              </span>
                              <span className='action-arrow'>
                                <ArrowRightIcon />
                              </span>
                            </button>
                          ))}
                      </div>
                    </>
                  )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default NewThreadModal;
